import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, AlertTriangle } from "lucide-react";
import { ScreenHeader } from "@/components/ScreenHeader";
import { api, API_BASE, getToken } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { colors, spacing, fonts } from "@/theme";

const ALL_TABS = [
  { key: "daily", label: "Daily", csvKind: "daily" },
  { key: "weekly", label: "Weekly", csvKind: "weekly" },
  { key: "weekly-by-driver", label: "By Driver", csvKind: "weekly_by_driver" },
  { key: "invoice", label: "Invoice", csvKind: "invoice" },
];

export default function Reports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const tabs = isManager ? ALL_TABS : ALL_TABS.filter((t) => t.key !== "invoice");
  const [tab, setTab] = useState("daily");
  const [rows, setRows] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yards, setYards] = useState([]);
  const [yardFilter, setYardFilter] = useState("all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (tab === "invoice") {
          const data = await api("/reports/invoice");
          setInvoice(data);
        } else {
          const data = await api(`/reports/${tab}`);
          setRows(data.rows);
        }
      } catch (e) { setRows([]); setInvoice(null); }
      setLoading(false);
    })();
  }, [tab]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/locations");
        setYards(r.yards || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const exportCsv = async (kind) => {
    const token = getToken();
    const url = `${API_BASE}/api/reports/export.csv?kind=${kind}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `gs-group-yard-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const isInvoice = tab === "invoice";
  const dataset = isInvoice ? (invoice?.rows || []) : rows;
  const countByYard = (y) => {
    if (isInvoice) return dataset.filter((r) => r.yard === y).reduce((sum, r) => sum + (r.quantity || 0), 0);
    return dataset.filter((r) => r.yard === y).reduce((sum, r) => sum + (r.count || 0), 0);
  };
  const totalCount = isInvoice ? (invoice?.total_moves || 0) : dataset.reduce((sum, r) => sum + (r.count || 0), 0);

  return (
    <div style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenHeader
        title="Reports"
        subtitle="Analytics & billing"
        right={isManager ? (
          <button data-testid="open-rates" onClick={() => navigate("/rates")} style={s.ratesBtn}>
            <Settings size={16} color={colors.onSurfaceInverse} />
            <span style={s.ratesBtnText}>Rates</span>
          </button>
        ) : undefined}
      />

      <div style={s.body}>
        <div style={s.tabsRow}>
          {tabs.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                style={{ ...s.tab, ...(active ? s.tabActive : {}) }}>
                <span style={{ ...s.tabText, color: active ? colors.onSurfaceInverse : colors.onSurface }}>{t.label}</span>
              </button>
            );
          })}
        </div>

        <div style={s.yardChipsRow}>
          <YardChip label="All yards" count={totalCount} active={yardFilter === "all"} onClick={() => setYardFilter("all")} testId="yard-all" />
          {yards.map((y) => (
            <YardChip key={y} label={y} count={countByYard(y)} active={yardFilter === y} onClick={() => setYardFilter(y)} testId={`yard-${y}`} />
          ))}
        </div>

        <div style={s.exportRow}>
          <button data-testid="export-raw" style={s.exportBtn} onClick={() => exportCsv("raw")}>
            <span style={s.exportText}>Export (CSV)</span>
          </button>
        </div>

        {loading ? (
          <div style={s.spinnerWrap}><div className="yo-spinner" /></div>
        ) : isInvoice ? (
          <InvoiceView
            data={invoice ? { ...invoice, rows: yardFilter === "all" ? invoice.rows : invoice.rows.filter((r) => r.yard === yardFilter) } : null}
            onConfigure={() => navigate("/rates")}
          />
        ) : (() => {
          const filteredRows = yardFilter === "all" ? rows : rows.filter((r) => r.yard === yardFilter);
          return filteredRows.length === 0 ? (
            <div style={s.empty}>No acknowledged moves{yardFilter !== "all" ? ` at ${yardFilter}` : ""} yet.</div>
          ) : (
            <ReportTable rows={filteredRows} kind={tab} />
          );
        })()}
      </div>
    </div>
  );
}

function YardChip({ label, count, active, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} style={{ ...s.yardChip, ...(active ? s.yardChipActive : {}) }}>
      <span style={{ ...s.yardChipText, color: active ? colors.onBrandPrimary : colors.onSurfaceTertiary }}>{label}</span>
      <span style={{ ...s.yardBadge, ...(active ? s.yardBadgeActive : {}) }}>
        <span style={{ ...s.yardBadgeText, color: active ? colors.brandPrimary : colors.onSurfaceTertiary }}>{count}</span>
      </span>
    </button>
  );
}

function ReportTable({ rows, kind }) {
  const columns =
    kind === "daily" ? ["date", "yard", "move_type", "count"] :
    kind === "weekly" ? ["week", "yard", "move_type", "count"] :
    ["week", "driver", "count"];

  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr style={s.theadRow}>
            {columns.map((c) => <th key={c} style={s.th}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${columns.map((c) => r[c]).join("|")}-${i}`} style={i % 2 === 1 ? { backgroundColor: colors.surfaceTertiary } : undefined}>
              {columns.map((c) => <td key={c} style={s.td}>{String(r[c] ?? "")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceView({ data, onConfigure }) {
  if (!data) return null;
  const total = (data.total_cents / 100).toFixed(2);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
      <div style={inv.headerBox}>
        <div>
          <div style={inv.headerLabel}>Week</div>
          <div style={inv.headerVal}>{data.week}</div>
        </div>
        <div>
          <div style={inv.headerLabel}>Moves</div>
          <div style={inv.headerVal}>{data.total_moves}</div>
        </div>
        <div>
          <div style={inv.headerLabel}>Revenue (CAD)</div>
          <div data-testid="invoice-total" style={{ ...inv.headerVal, color: colors.success }}>${total}</div>
        </div>
      </div>

      {data.unpriced_count > 0 && (
        <button onClick={onConfigure} style={inv.warn}>
          <AlertTriangle size={18} color={colors.warning} />
          <span style={inv.warnText}>{data.unpriced_count} unpriced moves · click to add rates</span>
        </button>
      )}

      {data.rows.length === 0 ? (
        <div style={s.empty}>No acknowledged moves this week.</div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr style={s.theadRow}>
                <th style={s.th}>Yard</th>
                <th style={s.th}>Move type</th>
                <th style={s.th}>Qty</th>
                <th style={s.th}>Rate</th>
                <th style={s.th}>Line total</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.yard}|${r.move_type}`} style={i % 2 === 1 ? { backgroundColor: colors.surfaceTertiary } : undefined}>
                  <td style={{ ...s.td, ...fonts.monoBold, color: colors.brandPrimary }}>{r.yard}</td>
                  <td style={s.td}>{r.move_type}</td>
                  <td style={s.td}>{r.quantity}</td>
                  <td style={{ ...s.td, color: r.priced ? colors.onSurface : colors.warning }}>{r.priced ? `$${(r.rate_cents / 100).toFixed(2)} CAD` : "—"}</td>
                  <td style={{ ...s.td, ...fonts.monoBold }}>${(r.line_total_cents / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={inv.totalRow}>
            <span style={inv.totalLabel}>Total due (CAD)</span>
            <span style={inv.totalVal}>${total}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  body: { padding: spacing.xl, display: "flex", flexDirection: "column", gap: spacing.md },
  ratesBtn: { display: "flex", alignItems: "center", gap: 6, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurfaceInverse, padding: `6px ${spacing.sm}px`, backgroundColor: "transparent", cursor: "pointer" },
  ratesBtnText: { ...fonts.monoBold, fontSize: 11, color: colors.onSurfaceInverse, letterSpacing: 0 },
  tabsRow: { display: "flex", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, maxWidth: 640 },
  tab: { flex: 1, padding: spacing.md, backgroundColor: colors.surfaceSecondary, border: "none", cursor: "pointer" },
  tabActive: { backgroundColor: colors.onSurface },
  tabText: { ...fonts.monoBold, fontSize: 12, letterSpacing: 0 },
  yardChipsRow: { display: "flex", flexWrap: "wrap", gap: spacing.sm },
  yardChip: { display: "flex", alignItems: "center", gap: spacing.xs, padding: `8px ${spacing.md}px`, borderWidth: 2, borderStyle: "solid", borderColor: colors.borderStrong, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  yardChipActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  yardChipText: { ...fonts.monoBold, fontSize: 10, letterSpacing: 0 },
  yardBadge: { minWidth: 22, padding: "1px 6px", borderWidth: 1, borderStyle: "solid", borderColor: colors.borderStrong, backgroundColor: colors.surfaceTertiary, textAlign: "center" },
  yardBadgeActive: { backgroundColor: colors.onBrandPrimary, borderColor: colors.onBrandPrimary },
  yardBadgeText: { ...fonts.monoBold, fontSize: 10, letterSpacing: 0 },
  exportRow: { display: "flex", gap: spacing.sm, maxWidth: 640 },
  exportBtn: { flex: 1, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.brandPrimary, padding: spacing.md, cursor: "pointer" },
  exportText: { ...fonts.monoBold, fontSize: 11, color: colors.onBrandPrimary, letterSpacing: 0 },
  spinnerWrap: { display: "flex", justifyContent: "center", marginTop: 40 },
  empty: { textAlign: "center", marginTop: 48, ...fonts.monoBold, color: colors.onSurfaceTertiary },
  tableWrap: { borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, overflowX: "auto", maxWidth: 900 },
  table: { borderCollapse: "collapse", width: "100%" },
  theadRow: { backgroundColor: colors.onSurface },
  th: { padding: spacing.md, ...fonts.monoBold, fontSize: 12, color: colors.onSurfaceInverse, textAlign: "left", letterSpacing: 0, whiteSpace: "nowrap" },
  td: { padding: spacing.md, ...fonts.mono, fontSize: 12, color: colors.onSurface, borderBottom: `1px solid ${colors.divider}`, whiteSpace: "nowrap" },
};

const inv = {
  headerBox: { display: "flex", justifyContent: "space-between", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, padding: spacing.lg, maxWidth: 640, gap: spacing.lg },
  headerLabel: { ...fonts.monoBold, fontSize: 10, color: colors.onSurfaceTertiary, letterSpacing: 0 },
  headerVal: { ...fonts.display, fontSize: 22, color: colors.onSurface, marginTop: 2 },
  warn: { display: "flex", alignItems: "center", gap: spacing.sm, borderWidth: 2, borderStyle: "solid", borderColor: colors.warning, backgroundColor: colors.onWarning, padding: spacing.md, cursor: "pointer", maxWidth: 640 },
  warnText: { ...fonts.monoBold, fontSize: 11, color: colors.warning, letterSpacing: 0 },
  totalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: colors.onSurface, padding: spacing.md },
  totalLabel: { ...fonts.display, fontSize: 14, color: colors.onSurfaceInverse, letterSpacing: 0 },
  totalVal: { ...fonts.display, fontSize: 22, color: colors.success, letterSpacing: 0 },
};
